// Payment wire Capture Response Model

import type { DeclineGuidance } from "../../neutral/outcomes/DeclineGuidance";
import { IReceiptData, IReceiptObjectData, ReceiptData, ReceiptObjectData, IEmvData, EmvData } from '../common/Receipt';

export interface ICaptureResponse {
  // Core transaction fields
  readonly merchid?: string;
  readonly token?: string;
  readonly respstat?: string;
  readonly respcode?: string;
  readonly resptext?: string;
  /**
   * Server-resolved decline guidance, attached by `attachDeclineGuidance` at
   * the post-auth convergence point on every NON-approved response. Absent on
   * approvals and on paths that never reach the cloud. Prefer it over
   * `resptext`, which is the gateway's own operator vocabulary.
   */
  readonly decline?: DeclineGuidance;

  readonly respproc?: string;
  readonly amount?: string;
  readonly authcode?: string | number;
  readonly account?: string;

  // Profile fields
  readonly profileid?: string;
  readonly acctid?: string;
  readonly defaultacct?: string;

  // Transaction reference fields
  readonly orderid?: string;
  readonly retref?: string;
  readonly batchid?: string;

  // Card and settlement info
  readonly commcard?: string;
  readonly setlstat?: string;

  // Fee information
  readonly fee_authcode?: string;
  readonly fee_format?: string;
  readonly fee_merchid?: string;
  readonly fee_retref?: string;
  readonly fee_type?: string;
  readonly fee_value?: string;
  readonly emvTagData?: IEmvData;

  // Receipt data
  readonly receipt?: IReceiptData;
  readonly receiptObj?: IReceiptObjectData;
}

export class CaptureResponse implements ICaptureResponse {
  // Core transaction fields
  readonly merchid?: string;
  readonly token?: string;
  readonly respstat?: string;
  readonly respcode?: string;
  readonly resptext?: string;
  /** See {@link ICaptureResponse.decline}. */
  readonly decline?: DeclineGuidance;
  readonly respproc?: string;
  readonly amount?: string;
  readonly authcode?: string | number;
  readonly account?: string;

  // Profile fields
  readonly profileid?: string;
  readonly acctid?: string;
  readonly defaultacct?: string;

  // Transaction reference fields
  readonly orderid?: string;
  readonly retref?: string;
  readonly batchid?: string;

  // Card and settlement info
  readonly commcard?: string;
  readonly setlstat?: string;

  // Fee information
  readonly fee_authcode?: string;
  readonly fee_format?: string;
  readonly fee_merchid?: string;
  readonly fee_retref?: string;
  readonly fee_type?: string;
  readonly fee_value?: string;
  readonly emvTagData?: EmvData;

  // Receipt data
  readonly receipt?: ReceiptData;
  readonly receiptObj?: ReceiptObjectData;

  constructor(data: ICaptureResponse) {
    // Core transaction fields
    this.merchid = data.merchid;
    this.token = data.token;
    this.respstat = data.respstat;
    this.respcode = data.respcode;
    this.resptext = data.resptext;
    this.decline = data.decline;
    this.respproc = data.respproc;
    this.amount = data.amount;
    this.authcode = data.authcode;
    this.account = data.account;

    // Profile fields
    this.profileid = data.profileid;
    this.acctid = data.acctid;
    this.defaultacct = data.defaultacct;

    // Transaction reference fields
    this.orderid = data.orderid;
    this.retref = data.retref;
    this.batchid = data.batchid;

    // Card and settlement info
    this.commcard = data.commcard;
    this.setlstat = data.setlstat;

    // Fee information
    this.fee_authcode = data.fee_authcode;
    this.fee_format = data.fee_format;
    this.fee_merchid = data.fee_merchid;
    this.fee_retref = data.fee_retref;
    this.fee_type = data.fee_type;
    this.fee_value = data.fee_value;
    if (data.emvTagData) {
      this.emvTagData = new EmvData(data.emvTagData);
    }

    // Receipt data
    if (data.receipt) {
      this.receipt = new ReceiptData(data.receipt);
    }
    if (data.receiptObj) {
      this.receiptObj = new ReceiptObjectData(data.receiptObj);
    }
  }

  // Status check methods
  isApproved(): boolean {
    return this.respstat === 'A';
  }

  isDeclined(): boolean {
    return this.respstat === 'C';
  }

  isRetry(): boolean {
    return this.respstat === 'B';
  }

  isSuccessful(): boolean {
    return this.isApproved();
  }

  // Settlement status methods
  isAuthorized(): boolean {
    return this.setlstat === 'Authorized';
  }

  isQueuedForCapture(): boolean {
    return this.setlstat === 'Queued for Capture';
  }

  isVoided(): boolean {
    return this.setlstat === 'Voided';
  }

  isZeroAmount(): boolean {
    return this.setlstat === 'Zero Amount';
  }

  canBeProcessed(): boolean {
    return this.isAuthorized() || this.isQueuedForCapture();
  }

  // Profile methods
  hasProfile(): boolean {
    return this.profileid !== undefined && this.profileid !== null && this.profileid !== '';
  }

  isDefaultAccount(): boolean {
    return this.defaultacct === 'Y';
  }

  getProfileIdentifier(): string | null {
    if (!this.hasProfile()) return null;
    return this.acctid ? `${this.profileid}/${this.acctid}` : this.profileid!;
  }

  // Fee methods
  hasFee(): boolean {
    return this.fee_type !== undefined && this.fee_type !== null && this.fee_type !== '';
  }

  isSurcharge(): boolean {
    return this.fee_type === 'SURCHRG';
  }

  isConvenienceFee(): boolean {
    return this.fee_type === 'CVNFEE';
  }

  isServiceFee(): boolean {
    return this.fee_type === 'SRVCFEE';
  }

  isFeeWaived(): boolean {
    return this.fee_type?.includes('_WAIVED') === true;
  }

  getFeeAmount(): number | null {
    if (!this.hasFee() || !this.fee_value) return null;
    return parseFloat(this.fee_value);
  }

  isFeePercentage(): boolean {
    return this.fee_format === 'percent';
  }

  isFeeFlat(): boolean {
    return this.fee_format === 'flat';
  }

  // Card methods
  isCorporateCard(): boolean {
    return this.commcard === 'Y';
  }

  isTokenized(): boolean {
    return this.token !== undefined && this.token !== null && this.token !== '';
  }

  // Amount methods
  getCapturedAmount(): number {
    return this.amount ? parseFloat(this.amount) : 0;
  }

  // Receipt methods
  hasReceipt(): boolean {
    return this.receipt !== undefined && this.receipt !== null;
  }

  hasReceiptObj(): boolean {
    return this.receiptObj !== undefined && this.receiptObj !== null;
  }

  getReceiptData(): any | null {
    return this.receipt || null;
  }

  getReceiptObjData(): any | null {
    return this.receiptObj || null;
  }

  getEMVData(): any | null {
    return this.emvTagData || null;
  }

  // Response code helpers
  getResponseCode(): string {
    return this.respcode || '';
  }

  getResponseText(): string {
    return this.resptext || '';
  }

  getProcessor(): string {
    return this.respproc || '';
  }

  // Reference methods
  getRetrievalReference(): string {
    return this.retref || '';
  }

  getBatchId(): string {
    return this.batchid || '';
  }

  getMerchantId(): string {
    return this.merchid || '';
  }

  // Utility methods
  requiresAction(): boolean {
    return this.isRetry() || this.isDeclined();
  }

  isComplete(): boolean {
    return this.isApproved() && this.isQueuedForCapture();
  }

  /**
   * Creates a plain JavaScript object suitable for API responses.
   */
  toJSON(): ICaptureResponse {
    const result: any = {};

    // Only include defined fields to keep the JSON clean
    if (this.merchid !== undefined) result.merchid = this.merchid;
    if (this.token !== undefined) result.token = this.token;
    if (this.respstat !== undefined) result.respstat = this.respstat;
    if (this.respcode !== undefined) result.respcode = this.respcode;
    if (this.resptext !== undefined) result.resptext = this.resptext;
    if (this.respproc !== undefined) result.respproc = this.respproc;
    if (this.amount !== undefined) result.amount = this.amount;
    if (this.authcode !== undefined) result.authcode = this.authcode;
    if (this.account !== undefined) result.account = this.account;
    if (this.profileid !== undefined) result.profileid = this.profileid;
    if (this.acctid !== undefined) result.acctid = this.acctid;
    if (this.defaultacct !== undefined) result.defaultacct = this.defaultacct;
    if (this.orderid !== undefined) result.orderid = this.orderid;
    if (this.retref !== undefined) result.retref = this.retref;
    if (this.batchid !== undefined) result.batchid = this.batchid;
    if (this.commcard !== undefined) result.commcard = this.commcard;
    if (this.setlstat !== undefined) result.setlstat = this.setlstat;
    if (this.fee_authcode !== undefined) result.fee_authcode = this.fee_authcode;
    if (this.fee_format !== undefined) result.fee_format = this.fee_format;
    if (this.fee_merchid !== undefined) result.fee_merchid = this.fee_merchid;
    if (this.fee_retref !== undefined) result.fee_retref = this.fee_retref;
    if (this.fee_type !== undefined) result.fee_type = this.fee_type;
    if (this.fee_value !== undefined) result.fee_value = this.fee_value;
    if (this.emvTagData !== undefined) result.emvTagData = this.emvTagData;
    if (this.receipt !== undefined) result.receipt = this.receipt;
    if (this.receiptObj !== undefined) result.receiptObj = this.receiptObj;

    return result as ICaptureResponse;
  }

  static fromJSON(data: Record<string, any>): CaptureResponse {
    const responseData = { ...data };
    if (typeof responseData.receipt === 'string') {
      responseData.receipt = JSON.parse(responseData.receipt);
    }
    if (typeof responseData.receiptObj === 'string') {
      responseData.receiptObj = JSON.parse(responseData.receiptObj);
    }
    if (typeof responseData.emvTagData === 'string') {
      responseData.emvTagData = JSON.parse(responseData.emvTagData);
    }
    return new CaptureResponse(responseData as ICaptureResponse);
  }

  // Static factory methods
  static createApproved(retref: string, amount: string, authcode?: string): CaptureResponse {
    return new CaptureResponse({
      respstat: 'A',
      retref,
      amount,
      authcode,
      setlstat: 'Queued for Capture'
    });
  }

  static createDeclined(respcode: string, resptext: string): CaptureResponse {
    return new CaptureResponse({
      respstat: 'C',
      respcode,
      resptext,
      setlstat: 'Declined'
    });
  }

  static createRetry(respcode?: string, resptext?: string): CaptureResponse {
    return new CaptureResponse({
      respstat: 'B',
      respcode,
      resptext
    });
  }
} 